/**
 * Authentication Store - Manages user authentication state
 * Replaces worker-based authentication with client-side auth
 */

import { create } from 'zustand';
import { subscribeWithSelector } from 'zustand/middleware';
import { clientAuth, type AuthUser, type LoginCredentials, type RegisterCredentials } from '../services/auth/clientAuth';

// Re-export AuthUser for use in other modules
export type { AuthUser };

export interface AuthState {
  // User state
  user: AuthUser | null;
  token: string | null;
  isAuthenticated: boolean;
  
  // Loading states
  isLoading: boolean;
  isLoggingIn: boolean;
  isRegistering: boolean;
  isLoggingOut: boolean;
  
  // Error state
  error: string | null;
  
  // Initialization
  isInitialized: boolean;
}

export interface AuthActions {
  // Authentication
  login: (credentials: LoginCredentials) => Promise<boolean>;
  register: (credentials: RegisterCredentials) => Promise<boolean>;
  logout: () => Promise<void>;
  
  // Profile management
  updateProfile: (updates: Partial<Pick<AuthUser, 'name' | 'avatar'>>) => Promise<boolean>;
  changePassword: (currentPassword: string, newPassword: string) => Promise<boolean>;
  deleteAccount: () => Promise<boolean>;
  
  // State management
  clearError: () => void;
  setLoading: (loading: boolean) => void;
  
  // Initialization
  initialize: () => Promise<void>;
}

export type AuthStore = AuthState & AuthActions;

const initialState: AuthState = {
  user: null,
  token: null,
  isAuthenticated: false,
  isLoading: false,
  isLoggingIn: false,
  isRegistering: false,
  isLoggingOut: false,
  error: null,
  isInitialized: false
};

export const useAuthStore = create<AuthStore>()(
  subscribeWithSelector((set, _get) => ({
    ...initialState,

    // Authentication
    login: async (credentials: LoginCredentials) => {
      set({ isLoggingIn: true, error: null });
      
      try {
        const result = await clientAuth.login(credentials);
        
        if (result.success && result.user && result.token) {
          set({
            user: result.user,
            token: result.token,
            isAuthenticated: true,
            isLoggingIn: false,
            error: null
          });
          return true;
        } else {
          set({
            error: result.error || 'Login failed',
            isLoggingIn: false
          });
          return false;
        }
      } catch (error) {
        const errorMessage = error instanceof Error ? error.message : 'Login failed';
        set({
          error: errorMessage,
          isLoggingIn: false
        });
        return false;
      }
    },

    register: async (credentials: RegisterCredentials) => {
      set({ isRegistering: true, error: null });
      
      try {
        const result = await clientAuth.register(credentials);
        
        if (result.success && result.user && result.token) {
          set({
            user: result.user,
            token: result.token,
            isAuthenticated: true,
            isRegistering: false,
            error: null
          });
          return true;
        } else {
          set({
            error: result.error || 'Registration failed',
            isRegistering: false
          });
          return false;
        }
      } catch (error) {
        const errorMessage = error instanceof Error ? error.message : 'Registration failed';
        set({
          error: errorMessage,
          isRegistering: false
        });
        return false;
      }
    },

    logout: async () => {
      set({ isLoggingOut: true });
      
      try {
        await clientAuth.logout();
        set({
          user: null,
          token: null,
          isAuthenticated: false,
          isLoggingOut: false,
          error: null
        });
      } catch (error) {
        console.error('Logout error:', error);
        // Even if logout fails, clear the local state
        set({
          user: null,
          token: null,
          isAuthenticated: false,
          isLoggingOut: false,
          error: null
        });
      }
    },

    // Profile management
    updateProfile: async (updates: Partial<Pick<AuthUser, 'name' | 'avatar'>>) => {
      set({ isLoading: true, error: null });
      
      try {
        const result = await clientAuth.updateProfile(updates);
        
        if (result.success && result.user) {
          set({
            user: result.user,
            isLoading: false,
            error: null
          });
          return true;
        } else {
          set({
            error: result.error || 'Profile update failed',
            isLoading: false
          });
          return false;
        }
      } catch (error) {
        const errorMessage = error instanceof Error ? error.message : 'Profile update failed';
        set({
          error: errorMessage,
          isLoading: false
        });
        return false;
      }
    },

    changePassword: async (currentPassword: string, newPassword: string) => {
      set({ isLoading: true, error: null });
      
      try {
        const result = await clientAuth.changePassword(currentPassword, newPassword);
        
        if (result.success) {
          set({
            isLoading: false,
            error: null
          });
          return true;
        } else {
          set({
            error: result.error || 'Password change failed',
            isLoading: false
          });
          return false;
        }
      } catch (error) {
        const errorMessage = error instanceof Error ? error.message : 'Password change failed';
        set({
          error: errorMessage,
          isLoading: false
        });
        return false;
      }
    },

    deleteAccount: async () => {
      set({ isLoading: true, error: null });
      
      try {
        const result = await clientAuth.deleteAccount();
        
        if (result.success) {
          set({
            user: null,
            token: null,
            isAuthenticated: false,
            isLoading: false,
            error: null
          });
          return true;
        } else {
          set({
            error: result.error || 'Account deletion failed',
            isLoading: false
          });
          return false;
        }
      } catch (error) {
        const errorMessage = error instanceof Error ? error.message : 'Account deletion failed';
        set({
          error: errorMessage,
          isLoading: false
        });
        return false;
      }
    },

    // State management
    clearError: () => {
      set({ error: null });
    },

    setLoading: (loading: boolean) => {
      set({ isLoading: loading });
    },

    // Initialization
    initialize: async () => {
      set({ isLoading: true });
      
      try {
        // Check if user is already authenticated
        const currentUser = clientAuth.getCurrentUser();
        const token = clientAuth.getToken();
        
        if (currentUser && token) {
          set({
            user: currentUser,
            token,
            isAuthenticated: true,
            isLoading: false,
            isInitialized: true
          });
        } else {
          set({
            user: null,
            token: null,
            isAuthenticated: false,
            isLoading: false,
            isInitialized: true
          });
        }
      } catch (error) {
        console.error('Auth initialization error:', error);
        set({
          user: null,
          token: null,
          isAuthenticated: false,
          isLoading: false,
          isInitialized: true,
          error: 'Failed to initialize authentication'
        });
      }
    }
  }))
);

// Utility selectors
export const useAuth = () => {
  const store = useAuthStore();
  return {
    user: store.user,
    isAuthenticated: store.isAuthenticated,
    isLoading: store.isLoading || store.isLoggingIn || store.isRegistering || store.isLoggingOut,
    error: store.error,
    isInitialized: store.isInitialized,
    login: store.login,
    register: store.register,
    logout: store.logout,
    updateProfile: store.updateProfile,
    changePassword: store.changePassword,
    deleteAccount: store.deleteAccount,
    clearError: store.clearError
  };
};

export const useAuthUser = () => useAuthStore(state => state.user);
export const useIsAuthenticated = () => useAuthStore(state => state.isAuthenticated);
export const useAuthLoading = () => useAuthStore(state => 
  state.isLoading || state.isLoggingIn || state.isRegistering || state.isLoggingOut
);
export const useAuthError = () => useAuthStore(state => state.error);

// Initialize auth on store creation
useAuthStore.getState().initialize();
